import { orderSourceLabel } from '@/utils/orderSource';
/**
 * printRunSheet — paper backup for a delivery run, printed before the driver
 * departs. One row per stop with a blank signature column, so it can stand
 * in for delivery confirmation if the app becomes unreachable mid-run.
 *
 * Not to be confused with printRunManifest, which is a post-delivery report
 * that pulls already-captured digital signatures from the Delivery Log.
 */
import { toast } from 'sonner';
import { escapeHtml as esc } from '@/utils/htmlEscape';
import { scheduleAutomaticPrint } from '@/utils/automaticPrint';

export const RUN_SHEET_POPUP_FEATURES = 'width=900,height=700';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

// "20260624-001" -> "001" — the literal digits right of the dash.
const orderNumberSuffix = (full) => {
  const m = String(full || '').match(/-(\d+)$/);
  return m ? m[1] : (full || '');
};

const isOfflineDraftRun = (run) => (
  run?.origin === 'local_desktop' || run?.is_offline_draft === true
);

export function buildRunSheetHtml(run) {
  const stops = run.populated_stops || [];
  const offlineDraft = isOfflineDraftRun(run);
  const driverName = run.driver?.name || (offlineDraft ? 'Offline Draft Run' : 'Unassigned Driver');
  const offlineDriverNotice = offlineDraft && run.user_id
    ? 'Offline proposed assignment — cloud/mobile confirmation pending.'
    : offlineDraft
      ? 'No driver proposed. Cloud/mobile confirmation pending.'
      : '';
  const titlePrefix = offlineDraft ? 'Offline Draft Run Sheet' : 'Delivery Signature Sheet';

  const rows = stops.map((stop) => {
    const order = stop.order || {};
    const orderNumber = esc(orderNumberSuffix(order.internal_order_number || order.order_number));
    const source = esc(orderSourceLabel(order, ''));
    const recipient = esc(order.recipient_name || '');
    const streetLine = esc([order.recipient_street_address, order.recipient_street_address_2].filter(Boolean).join(', '));
    const zipLine = esc(order.recipient_zip_code || '');
    const phone = esc(order.recipient_phone || '');

    return `<tr>
      <td>${orderNumber}</td>
      <td>${source}</td>
      <td>${recipient}</td>
      <td>${streetLine}${zipLine ? `<br/>${zipLine}` : ''}</td>
      <td>${phone}</td>
      <td class="sig-cell"></td>
    </tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(titlePrefix)} - ${esc(run.date || '')}-${esc(run.name || 'Delivery Run')}-${esc(driverName)}</title>
<style>
  /* Paper and orientation belong to the print dialog. Fit even portrait Letter. */
  @page { margin: 0.4in; }
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111;
    font-family: Arial, Helvetica, sans-serif; max-width: 100%; overflow-wrap: anywhere; }
  .header { display: flex; justify-content: space-between; align-items: baseline;
    gap: 12px; max-width: 100%;
    border-bottom: 2px solid #111; padding-bottom: 0.12in; margin-bottom: 0.2in; }
  .header > div { min-width: 0; flex: 1 1 0; }
  .header h1 { font-size: 18pt; margin: 0; }
  .header .meta { font-size: 11pt; text-align: right; }
  .offline-badge { display: inline-block; margin-top: 0.08in; padding: 3px 8px;
    border: 1px solid #c2410c; border-radius: 999px; color: #9a3412;
    background: #ffedd5; font-size: 9pt; font-weight: bold; letter-spacing: 0.3px;
    text-transform: uppercase; }
  .offline-note { margin: -0.08in 0 0.16in 0; padding: 0.08in 0.12in;
    border: 1px solid #fdba74; background: #fff7ed; color: #7c2d12; font-size: 10pt; }
  table { width: 100%; max-width: 100%; border-collapse: collapse; font-size: 10pt; table-layout: fixed; }
  th, td { border: 1px solid #999; padding: 8px 8px; text-align: left; vertical-align: top;
    line-height: 1.3; overflow-wrap: anywhere; }
  td { height: 0.6in; }
  th { background: #eee; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.5px; }
  @media print {
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${esc(driverName)}</h1>
      ${offlineDraft ? '<span class="offline-badge">Offline Draft</span>' : ''}
    </div>
    <div class="meta">
      ${run.date ? `${esc(fmtDate(run.date))}<br/>` : ''}
      Run: ${esc(run.name || 'Delivery Run')}
    </div>
  </div>
  ${offlineDraft ? `<div class="offline-note"><strong>${esc(offlineDriverNotice)}</strong><br/>This route was created locally while offline. Use this paper sheet for driver dispatch and signatures until the app syncs.</div>` : ''}
  <table>
    <colgroup>
      <col style="width:8%">
      <col style="width:9%">
      <col style="width:17%">
      <col style="width:26%">
      <col style="width:12%">
      <col style="width:28%">
    </colgroup>
    <thead>
      <tr>
        <th>Order #</th>
        <th>Src</th>
        <th>Recipient</th>
        <th>Address</th>
        <th>Phone</th>
        <th>Signature</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="6" style="text-align:center;color:#888;">No stops</td></tr>'}
    </tbody>
  </table>
</body>
</html>`;

  return html;
}

export function printRunSheet(run, { popup, isCurrent = () => true, onSettled = () => {} } = {}) {
  if (!run) {
    onSettled();
    return { cancelAutoPrint: () => {} };
  }

  const html = buildRunSheetHtml(run);
  const w = popup === undefined ? window.open('', '_blank', RUN_SHEET_POPUP_FEATURES) : popup;
  if (!w) {
    toast.error('Pop-ups blocked — allow pop-ups to print');
    onSettled();
    return { cancelAutoPrint: () => {} };
  }
  if (w.closed || !isCurrent()) {
    onSettled();
    return { cancelAutoPrint: () => {} };
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return scheduleAutomaticPrint({ popup: w, isCurrent, onSettled });
}
